/* Supply › Reports › Sales › Sales by Channel
 * Sales totals grouped by channel (B2C / CHE / Eyewear / B2B) over a date range. */
import React, { useEffect, useMemo, useState } from "react";
import supplyService from "../../../Services/supplyService";
import { cbFmtINR, cbFmtNum } from "../supplyData";
import {
  Button,
  Pill,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
} from "../supplyKit";
import { exportToXlsx, inDateRange } from "./reportUtils";
import { ReportBack } from "./reportShared";
import { KIND_LABEL } from "./SalesRegister";
import { PHARMACY_ROUTES } from "../../../Route/path";

const SalesByChannel = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [range, setRange] = useState({ fromDate: "", toDate: "" });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    supplyService
      .getInvoices()
      .then((rows) => {
        if (!alive) return;
        setInvoices(Array.isArray(rows) ? rows : []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load sales report.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  const groups = useMemo(() => {
    const scoped = invoices.filter((inv) => inDateRange(inv.issued, range.fromDate, range.toDate));
    const map = {};
    scoped.forEach((inv) => {
      const k = inv.kind || "other";
      if (!map[k]) map[k] = { kind: k, count: 0, amount: 0, paid: 0 };
      map[k].count += 1;
      map[k].amount += Number(inv.amount) || 0;
      map[k].paid += Number(inv.paid) || 0;
    });
    return Object.values(map).sort((a, b) => b.amount - a.amount);
  }, [invoices, range]);

  const totals = groups.reduce(
    (acc, g) => {
      acc.count += g.count;
      acc.amount += g.amount;
      acc.paid += g.paid;
      return acc;
    },
    { count: 0, amount: 0, paid: 0 }
  );

  const handleExport = () => {
    const headers = ["Channel", "Invoices", "Sales value", "Collected", "Outstanding"];
    const rows = groups.map((g) => [
      KIND_LABEL[g.kind] || g.kind,
      g.count,
      Number(g.amount.toFixed(2)),
      Number(g.paid.toFixed(2)),
      Number((g.amount - g.paid).toFixed(2)),
    ]);
    exportToXlsx("sales-by-channel.xlsx", headers, rows, "By channel");
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.SALES_REPORT} label="Sales Reports" />
      <SectionHeader
        title="Sales by Channel"
        sub="Sales totals grouped by channel over a date range"
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!groups.length}>
            Export
          </Button>
        }
      />

      <ToolbarBar>
        <DateRangeFilter
          from={range.fromDate}
          to={range.toDate}
          onFromChange={(v) => setRange((r) => ({ ...r, fromDate: v }))}
          onToChange={(v) => setRange((r) => ({ ...r, toDate: v }))}
        />
        <Button onClick={() => setRange({ fromDate: "", toDate: "" })}>Reset</Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(totals.count)} invoices
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-3" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Channels" value={cbFmtNum(groups.length)} variant="ink" />
        <KpiBox label="Total sales" value={cbFmtINR(totals.amount)} variant="green" />
        <KpiBox label="Outstanding" value={cbFmtINR(totals.amount - totals.paid)} variant="amber" />
      </div>

      {loading ? (
        <LoadingState label="Loading sales by channel…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <Table
          columns={[
            { label: "Channel" },
            { label: "Invoices", align: "right" },
            { label: "Sales value", align: "right" },
            { label: "Collected", align: "right" },
            { label: "Outstanding", align: "right" },
            { label: "Share", align: "right" },
          ]}
        >
          {groups.length === 0 && (
            <tr>
              <Td colSpan={6} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                No sales in this range.
              </Td>
            </tr>
          )}
          {groups.map((g) => {
            const due = g.amount - g.paid;
            const share = totals.amount ? Math.round((g.amount / totals.amount) * 100) : 0;
            return (
              <Tr key={g.kind}>
                <Td>
                  <Pill variant="neutral">{KIND_LABEL[g.kind] || g.kind}</Pill>
                </Td>
                <Td mono right>
                  {cbFmtNum(g.count)}
                </Td>
                <Td mono right style={{ fontWeight: 600 }}>
                  {cbFmtINR(g.amount)}
                </Td>
                <Td mono right>
                  {cbFmtINR(g.paid)}
                </Td>
                <Td mono right style={{ color: due > 0 ? "var(--amber-700)" : "var(--fg-muted)" }}>
                  {cbFmtINR(due)}
                </Td>
                <Td mono right>
                  {share}%
                </Td>
              </Tr>
            );
          })}
        </Table>
      )}
    </div>
  );
};

export default SalesByChannel;
